<template>
  <div>
    <h2 class="text-2xl font-semibold mb-4">投资分析</h2>

    <el-card shadow="hover">
      <template #header>
        <div class="flex items-start md:items-center justify-between gap-3 flex-col md:flex-row">
          <h2 class="text-lg font-semibold">融资计划（投资分析）</h2>
          <div class="flex items-center gap-2 flex-wrap">
            <el-dropdown class="w-full sm:w-auto" @command="onSelectExample">
              <el-button size="small" plain>
                示例方案选择
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item v-for="ex in examples" :key="ex.key" :command="ex.key">
                    {{ ex.title }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>
      </template>

      <el-alert type="info" :closable="false" class="mb-4" title="用途：用于评估项目的现金流、NPV 与 IRR；支持直线法和双倍余额递减折旧。">
        <div class="text-sm text-gray-700 mt-2">
          本组件用于生成并分析项目的年度现金流、计算净现值（NPV）与内部收益率（IRR），支持可选折旧方法、运营资本处理与期末残值回收。适用场景包括：固定资产投资评估、项目可行性分析、教学示例与企业内部投资审批的初步估算。
        </div>
      </el-alert>

      <FormulaPanel :title="'项目财务公式'" :formulas="[npvFormula, irrFormula, ocfFormula, depreciationSL]"
        :variables="variablesMap" :collapsible="true" :autoCollapseOnMobile="true" size="compact" />

      <div class="grid grid-cols-1 gap-4">
        <div class="p-4">
          <h3 class="font-medium mb-2">项目参数</h3>
          <el-form :model="form" label-position="top" class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <el-form-item :label="`固定资产投资（${symbol}）`">
              <el-input-number v-model="costUI" :min="0" :step="10000" controls-position="right" />
            </el-form-item>
            <el-form-item label="使用年限（年）">
              <el-input-number v-model="form.lifeYears" :min="1" />
            </el-form-item>
            <el-form-item :label="`残值（${symbol}）`">
              <el-input-number v-model="salvageUI" :min="0" />
            </el-form-item>
            <el-form-item :label="`期末资产回收（${symbol}）`">
              <el-input-number v-model="realizedSalvageUI" :min="0" />
            </el-form-item>
            <el-form-item label="年销量（份）">
              <el-input-number v-model="form.annualSalesUnits" :min="0" />
            </el-form-item>
            <el-form-item :label="`单价（${symbol}）`">
              <el-input-number v-model="pricePerUnitUI" :min="0" />
            </el-form-item>
            <el-form-item :label="`固定成本（${symbol}/年）`">
              <el-input-number v-model="fixedCostUI" :min="0" />
            </el-form-item>
            <el-form-item :label="`可变成本（${symbol}/份）`">
              <el-input-number v-model="variableCostPerUnitUI" :min="0" />
            </el-form-item>
            <el-form-item :label="`期初运营资本（${symbol}）`">
              <el-input-number v-model="workingCapitalUI" :min="0" />
            </el-form-item>
            <el-form-item label="所得税率">
              <el-input-number v-model="form.taxRate" :min="0" :max="1" :step="0.01" />
            </el-form-item>
            <el-form-item label="资本成本(折现率)">
              <el-input-number v-model="form.discountRate" :min="0" :max="1" :step="0.01" />
            </el-form-item>
            <el-form-item label="折旧方法">
              <el-select v-model="form.depreciationMethod" placeholder="选择折旧方法">
                <el-option label="直线法 (straight-line)" value="straight" />
                <el-option label="双倍余额递减 (double-declining)" value="double" />
              </el-select>
            </el-form-item>
            <div class="md:col-span-3 flex gap-2 mt-1 items-center">
              <el-button type="primary" @click="runEvaluation">计算</el-button>
              <el-button type="success" @click="exportCSV" :disabled="!result">导出 CSV</el-button>
            </div>
          </el-form>
        </div>
      </div>



      <!-- 示例方案弹窗（移动到外层，确保在未计算时也能弹出） -->
      <el-dialog v-model="dialogVisible" :title="dialogTitle" width="640px">
        <div class="space-y-3">
          <el-alert type="info" :closable="false" show-icon>
            <template #title>
              {{ dialogTitle }}（示例）
            </template>
            <div class="text-sm text-gray-600 leading-relaxed" v-html="dialogDesc"></div>
          </el-alert>

          <el-form label-position="top" class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <el-form-item :label="`固定资产投资（${symbol}）`">
              <el-input-number v-model="exCostUI" :min="0" :step="10000" />
            </el-form-item>
            <el-form-item :label="`期初运营资本（${symbol}）`">
              <el-input-number v-model="exWorkingCapitalUI" :min="0" :step="1000" />
            </el-form-item>
            <el-form-item label="使用年限（年）">
              <el-input-number v-model="exLifeYearsUI" :min="1" />
            </el-form-item>
            <el-form-item label="年销量（份）">
              <el-input-number v-model="exAnnualSalesUnitsUI" :min="0" />
            </el-form-item>
            <el-form-item :label="`单价（${symbol}）`">
              <el-input-number v-model="exPricePerUnitUI" :min="0" />
            </el-form-item>
            <el-form-item :label="`变动成本（${symbol}/份）`">
              <el-input-number v-model="exVariableCostPerUnitUI" :min="0" />
            </el-form-item>
            <el-form-item :label="`固定成本（${symbol}/年）`">
              <el-input-number v-model="exFixedCostUI" :min="0" />
            </el-form-item>
            <el-form-item :label="`残值（${symbol}）`">
              <el-input-number v-model="exSalvageUI" :min="0" />
            </el-form-item>
            <el-form-item :label="`期末资产回收（${symbol}）`">
              <el-input-number v-model="exRealizedSalvageUI" :min="0" />
            </el-form-item>
            <el-form-item label="税率">
              <el-input-number v-model="exTaxRateUI" :min="0" :max="1" :step="0.01" />
            </el-form-item>
          </el-form>
        </div>
        <template #footer>
          <div class="flex items-center justify-end gap-2">
            <el-button @click="dialogVisible = false">取消</el-button>
            <el-button type="primary" @click="applyExample">确认并计算</el-button>
          </div>
        </template>
      </el-dialog>

      <el-collapse-transition>
        <div v-show="result" class="p-4">
          <el-card shadow="never" body-class="p-0">
            <div ref="chartEl" style="height:320px;"></div>
            <div class="text-xs text-gray-500 p-3">图表说明：OCF 为经营性现金流，反映经营活动产生的现金净流入；NCF为净现金流，包含期初投资与期末运营资本回收等非经营性现金流。
            </div>
          </el-card>
        </div>
      </el-collapse-transition>

      <div class="mt-6 p-4">
        <h3 class="font-medium mb-2">现金流明细</h3>
        <div class="text-xs text-gray-500 mb-2">单位：万元（按当前货币 {{ symbol }} 显示）</div>
        <el-table v-if="result" :data="displayTableRows" style="width:100%">
          <el-table-column prop="year" label="期次" width="80" />
          <el-table-column prop="ebit" label="EBIT（万元）" />
          <el-table-column prop="depreciation" label="折旧（万元）" />
          <el-table-column prop="netProfit" label="净利润（万元）" />
          <el-table-column prop="ocf" label="经营性现金流（万元）" />
          <el-table-column prop="cashFlow" label="净现金流（万元）" />
        </el-table>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue'
import * as echarts from 'echarts'
import { evaluateProject } from '@/lib/investmentEngine'
import FormulaPanel from '@/components/ui/FormulaPanel.vue'
import { useCurrency } from '@/composables/useCurrency'

const form = ref({
  cost: 2800000,
  lifeYears: 7,
  salvage: 0,
  // 期末实际回收值（用户可配置），默认与会计残值相同；单位为基准币（内部存储为基准 CNY）
  realizedSalvage: 0,
  annualSalesUnits: 4000,
  pricePerUnit: 5000,
  fixedCost: 3100000,
  variableCostPerUnit: 3750,
  workingCapital: 2200000,
  taxRate: 0.4,
  discountRate: 0.15,
  // 初始默认折旧方法为直线法，满足“初始的时候折旧方法默认为直线型”
  depreciationMethod: 'straight'
})

// ensure realizedSalvage defaults to salvage on initial load
if (!form.value.realizedSalvage) form.value.realizedSalvage = form.value.salvage || 0

const result = ref(null)
const tableRows = ref([])
const chartEl = ref(null)
let chart = null

// 货币处理：使用全局 useCurrency，所有显示/导入金额都遵循导航栏的货币选择
const { toDisplay, toBase, symbol } = useCurrency()

// UI 绑定（显示为当前货币，内部以基准 CNY 存储）
const costUI = computed({ get: () => toDisplay(form.value.cost), set: v => form.value.cost = toBase(v) })
const salvageUI = computed({ get: () => toDisplay(form.value.salvage || 0), set: v => form.value.salvage = toBase(v) })
const pricePerUnitUI = computed({ get: () => toDisplay(form.value.pricePerUnit || 0), set: v => form.value.pricePerUnit = toBase(v) })
const fixedCostUI = computed({ get: () => toDisplay(form.value.fixedCost || 0), set: v => form.value.fixedCost = toBase(v) })
const variableCostPerUnitUI = computed({ get: () => toDisplay(form.value.variableCostPerUnit || 0), set: v => form.value.variableCostPerUnit = toBase(v) })
const workingCapitalUI = computed({ get: () => toDisplay(form.value.workingCapital || 0), set: v => form.value.workingCapital = toBase(v) })

// 将基准币金额转换为当前货币并按“万元”显示
const toWanDisplay = (v) => (typeof v === 'number' ? Number((toDisplay(v) / 10000).toFixed(2)) : 0)


// 精简后的公式与变量（只保留教学常用核心）
const npvFormula = String.raw`NPV = \sum_{t=0}^{T} \dfrac{CF_t}{(1+r)^t}`
const irrFormula = String.raw`\text{IRR: } \sum_{t=0}^{T} \dfrac{CF_t}{(1+r)^t} = 0`
const ocfFormula = String.raw`OCF_t = (Rev_t - Var_t - Fixed - D_t)(1 - tax) + D_t`
const depreciationSL = String.raw`D = (Cost - Salvage) / Life`

const variablesMap = [
  { symbol: 'CF', label: '第 t 年净现金流（含经营现金流、投资与运营资本变化）' },
  { symbol: 'OCF', label: '经营性现金流：经营活动产生的现金净流入（不含期初设备投资与期末运营资本回收）' },
  { symbol: 'r', label: '资本成本（年化）' },
  { symbol: 'tax', label: '所得税率（例如 0.25 表示 25%）' },
  { symbol: 'D', label: '第 t 年折旧额' },
  { symbol: 'Rev', label: '第 t 年销售收入（销量 × 单价）' },
  { symbol: 'Var', label: '第 t 年变动成本（单位变动成本 × 销量）' },
  { symbol: 'Fixed', label: '固定成本（每年不随销量变化的成本）' }
]

// 示例方案（PC1000 项目）
const examples = ref([
  {
    key: 'pc1000',
    title: 'PC1000项目示例',
    desc: `一家公司正在考虑投资280万元购买设备，这项设备的预期寿命为7年，将使用直线法折旧，而没有任何残值。<br/>
预计每年销售4000台，每台售价5000元。<br/>
固定成本（不包括设备折旧）为每年310万元，可变成本为每台3750元。<br/>
运营资本（Working capital）期初投入为220万元；公司按40%的税率缴纳所得税，资本成本(折现率)为15%。`,
    form: {
      cost: 2800000,
      lifeYears: 7,
      salvage: 0,
      realizedSalvage: 0,
      annualSalesUnits: 4000,
      pricePerUnit: 5000,
      fixedCost: 3100000,
      variableCostPerUnit: 3750,
      workingCapital: 2200000,
      taxRate: 0.4,
      discountRate: 0.15,
      depreciationMethod: 'straight'
    }
  }
  ,
  {
    key: 'class_large_project',
    title: '大型项目示例（5年期）',
    desc: `某项目固定资产投资4500万元，流动资产投资2000万元；5年期，每年销量3万件，单价1800元，变动成本800元/件，除折旧外固定成本200万元；直线折旧，期末主观残值500万元（预计市场售价600万元）；所得税率33%，资本成本15%。`,
    form: {
      cost: 45000000,
      workingCapital: 20000000,
      lifeYears: 5,
      annualSalesUnits: 30000,
      pricePerUnit: 1800,
      variableCostPerUnit: 800,
      fixedCost: 2000000,
      salvage: 5000000,
      realizedSalvage: 5670000,
      taxRate: 0.33,
      discountRate: 0.15,
      depreciationMethod: 'straight'
    }
  }
])

const dialogVisible = ref(false)
const dialogTitle = ref('')
const dialogDesc = ref('')
const exForm = ref({})

const onSelectExample = (key) => {
  const ex = examples.value.find(e => e.key === key)
  if (!ex) return
  dialogTitle.value = ex.title
  dialogDesc.value = ex.desc
  exForm.value = { ...ex.form }
  dialogVisible.value = true
}

const applyExample = () => {
  form.value = { ...exForm.value }
  dialogVisible.value = false
  // mimic StandardCalculator: confirm should also run calculation
  runEvaluation()
}

// removed loadExample; examples now provided via the examples dropdown and dialog

function runEvaluation() {
  const res = evaluateProject({
    ...form.value,
    depreciationMethod: form.value.depreciationMethod,
    // 将用户配置的期末实际回收值传入评估（若未设置，evaluateProject 会使用 salvage 作为默认）
    terminalRecovery: form.value.realizedSalvage
  })
  result.value = res
  tableRows.value = res.ebitList.map((ebit, idx) => ({
    year: idx + 1,
    ebit,
    depreciation: res.depreciationSchedule[idx] ? res.depreciationSchedule[idx].depreciation : 0,
    netProfit: res.netProfitList[idx],
    ocf: res.operatingCashFlowList[idx],
    cashFlow: res.cashFlows[idx + 1] // cashFlows t=0 为投资
  }))
  nextTick(() => renderChart())
}

function renderChart() {
  if (!result.value || !chartEl.value) return
  if (!chart) chart = echarts.init(chartEl.value)
  const years = tableRows.value.map(r => 'Y' + r.year)
  const toWan = (v) => (typeof v === 'number' ? Number((toDisplay(v) / 10000).toFixed(2)) : null)
  const ocf = tableRows.value.map(r => toWan(r.ocf))
  const cashFlows = [toWan(result.value.cashFlows[0])].concat(tableRows.value.map(r => toWan(r.cashFlow)))
  chart.setOption({
    tooltip: { trigger: 'axis', valueFormatter: (v) => (v == null ? '' : `${v} 万元`) },
    legend: { data: ['OCF', 'NCF'] },
    xAxis: { type: 'category', data: ['T0'].concat(years) },
    yAxis: { type: 'value' },
    series: [
      { name: 'OCF', type: 'bar', data: [null].concat(ocf) },
      { name: 'NCF', type: 'line', data: cashFlows }
    ]
  })
}

// 示例弹窗的 UI 绑定（显示货币）
const exCostUI = computed({ get: () => toDisplay(exForm.value.cost || 0), set: v => exForm.value.cost = toBase(v) })
const exWorkingCapitalUI = computed({ get: () => toDisplay(exForm.value.workingCapital || 0), set: v => exForm.value.workingCapital = toBase(v) })
const exLifeYearsUI = computed({ get: () => exForm.value.lifeYears || 0, set: v => exForm.value.lifeYears = Number(v) })
const exAnnualSalesUnitsUI = computed({ get: () => exForm.value.annualSalesUnits || 0, set: v => exForm.value.annualSalesUnits = Number(v) })
const exPricePerUnitUI = computed({ get: () => toDisplay(exForm.value.pricePerUnit || 0), set: v => exForm.value.pricePerUnit = toBase(v) })
const exVariableCostPerUnitUI = computed({ get: () => toDisplay(exForm.value.variableCostPerUnit || 0), set: v => exForm.value.variableCostPerUnit = toBase(v) })
const exFixedCostUI = computed({ get: () => toDisplay(exForm.value.fixedCost || 0), set: v => exForm.value.fixedCost = toBase(v) })
const exSalvageUI = computed({ get: () => toDisplay(exForm.value.salvage || 0), set: v => exForm.value.salvage = toBase(v) })
const exRealizedSalvageUI = computed({ get: () => toDisplay(exForm.value.realizedSalvage ?? (exForm.value.salvage || 0)), set: v => exForm.value.realizedSalvage = toBase(v) })
// 主表单的期末回收 UI 绑定
const realizedSalvageUI = computed({ get: () => toDisplay(form.value.realizedSalvage ?? (form.value.salvage || 0)), set: v => form.value.realizedSalvage = toBase(v) })
const exTaxRateUI = computed({ get: () => exForm.value.taxRate || 0, set: v => exForm.value.taxRate = Number(v) })

// 计算后自动渲染图表（无需开关）

// 以万元展示的表格行
const displayTableRows = computed(() => {
  return (tableRows.value || []).map(r => ({
    year: r.year,
    ebit: toWanDisplay(r.ebit),
    depreciation: toWanDisplay(r.depreciation),
    netProfit: toWanDisplay(r.netProfit),
    ocf: toWanDisplay(r.ocf),
    cashFlow: toWanDisplay(r.cashFlow),
  }))
})

function exportCSV() {
  if (!result.value) return
  // Create wide table like the provided image; values in 万元 (display currency)
  const T = result.value.cashFlows.length - 1
  const years = ['第0年']
  for (let i = 1; i <= T; i++) years.push('第' + i + '年')
  // 将基准值转换为显示货币后再除以 10000 得到“万元”单位
  const toWan = v => (typeof v === 'number' ? (toDisplay(v) / 10000).toFixed(2) : '')
  // Rows: 销量, 单价, 销售收入, 固定成本, 折旧费, 变动成本, 总经营费用, 税前利润, 税后利润, 经营现金流, 设备投资, 运营资本投资, 净现金流
  const rows = []
  rows.push([`金额单位：${symbol} 万元`])
  rows.push(['', ...years])

  // helper to build a year-array with t0 empty
  const yearArray = (getter) => {
    const arr = ['']
    for (let i = 0; i < T; i++) {
      arr.push(getter(i))
    }
    return arr
  }

  // (1)销量
  rows.push(['(1)销量', ...yearArray(() => form.value.annualSalesUnits)])
  // (2)单价（元）
  rows.push(['(2)单价（元）', ...yearArray(() => form.value.pricePerUnit)])
  // (3)销售收入（万元）
  rows.push(['(3)销售收入（万元）', ...yearArray(() => toWan(form.value.annualSalesUnits * form.value.pricePerUnit))])

  // (4)固定成本（万元）
  rows.push(['(4)固定成本（万元）', ...yearArray(() => toWan(form.value.fixedCost))])

  // (5)折旧费（万元） - 从 depreciationSchedule 取值
  const depPerYear = (result.value.depreciationSchedule || []).map(d => d.depreciation)
  rows.push(['(5)折旧费（万元）', ...yearArray(i => toWan(depPerYear[i] || 0))])

  // (6)变动成本（万元）
  rows.push(['(6)变动成本（万元）', ...yearArray(() => toWan(form.value.variableCostPerUnit * form.value.annualSalesUnits))])

  // (7)总经营费用（万元） = 固定 + 变动 + 折旧
  rows.push(['(7)总经营费用（万元）', ...yearArray(i => {
    const dep = depPerYear[i] || 0
    return toWan(form.value.fixedCost + form.value.variableCostPerUnit * form.value.annualSalesUnits + dep)
  })])

  // (8)税前利润（万元） = EBIT
  rows.push(['(8)税前利润（万元）', ...yearArray(i => toWan(result.value.ebitList[i] || 0))])

  // (9)税后利润（万元）
  rows.push(['(9)税后利润（万元）', ...yearArray(i => toWan(result.value.netProfitList[i] || 0))])

  // (10)经营现金流（万元）
  rows.push(['(10)经营现金流（万元）', ...yearArray(i => toWan(result.value.operatingCashFlowList[i] || 0))])

  // (11)固定资产投资（万元） - 期初为负值
  const invRow = ['(11)固定资产投资（万元）']
  invRow.push(toWan(-form.value.cost))
  // 中间年份为空，最后一年若存在残值则在该列显示为正值（期末处置回收）
  for (let i = 1; i < T; i++) invRow.push('')
  const salvageVal = Number((form.value.realizedSalvage ?? form.value.salvage) || 0)
  invRow.push(salvageVal > 0 ? toWan(salvageVal) : '')
  rows.push(invRow)

  // (12)运营资本投资（万元） - 期初投入为负，期末回收为正
  const wcRow = ['(12)运营资本投资（万元）']
  wcRow.push(toWan(-form.value.workingCapital || 0))
  for (let i = 1; i < T; i++) wcRow.push('')
  wcRow.push(toWan(form.value.workingCapital || 0))
  rows.push(wcRow)

  // (13)净现金流（万元） - 直接使用 evaluateProject 返回的 cashFlows
  rows.push(['(13)净现金流（万元）', ...result.value.cashFlows.map(c => toWan(c))])

  // 空行分隔
  rows.push([''])
  // 追加指标：NPV、IRR、投资回收期
  const npvWan = toWan(result.value.npv)
  const irrPct = (typeof result.value.irr === 'number' && !isNaN(result.value.irr)) ? (result.value.irr * 100).toFixed(2) + '%' : ''
  const pb = (typeof result.value.paybackPeriod === 'number' && !isNaN(result.value.paybackPeriod)) ? result.value.paybackPeriod.toFixed(2) : ''
  rows.push([`NPV（@${(form.value.discountRate * 100).toFixed(0)}%）`, npvWan])
  rows.push(['内部收益率 IRR', irrPct])
  rows.push(['投资回收期（年）', pb])

  // Build CSV string with BOM to avoid Excel乱码
  const BOM = '\uFEFF'
  const csv = rows.map(r => r.join(',')).join('\n')
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = '融资计划_现金流.csv'
  a.click()
  URL.revokeObjectURL(url)
}

onMounted(() => { /* no automatic example load; user picks an example from the dropdown */ })
</script>

<style scoped></style>
